import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { AthleteProfile, SportType } from '../types';
import { Calendar, TrendingUp, Sparkles, Activity, Target, Zap } from 'lucide-react';
import { ProRestrictedWrapper } from './common/ProRestrictedWrapper';

interface ProgressDataPoint {
  date: Date;
  dateStr: string;
  scoringAvg: number;
  winRate: number;
  efficiency: number;
  xpEarned: number;
  gamesCount: number;
}

export type SeasonFilter = 'current' | 'last' | 'alltime';

interface PlayerProgressD3ChartProps {
  user: AthleteProfile;
  selectedSport: SportType;
  initialSeason?: SeasonFilter;
  onOpenProCheckout?: (contextMessage?: string) => void;
}

export const PlayerProgressD3Chart: React.FC<PlayerProgressD3ChartProps> = ({
  user,
  selectedSport,
  initialSeason = 'current',
  onOpenProCheckout,
}) => {
  const [metric, setMetric] = useState<'scoringAvg' | 'winRate' | 'efficiency' | 'xpEarned'>('scoringAvg');
  const [season, setSeason] = useState<SeasonFilter>(initialSeason);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [tooltipData, setTooltipData] = useState<{
    dateStr: string;
    value: number;
    metricLabel: string;
    gamesCount: number;
    x: number;
    y: number;
  } | null>(null);

  // Generate trend data tailored to the athlete, sport, and selected season
  const generateSeasonData = (): ProgressDataPoint[] => {
    const points: ProgressDataPoint[] = [];
    const today = new Date();

    const baseScoring = user.stats?.basketball?.pts
      ? Math.max(12, Math.round(user.stats.basketball.pts / (user.stats.basketball.gamesPlayed || 1)))
      : 18;

    if (season === 'alltime') {
      // 12 monthly data points representing career trajectory
      let runningScoring = Math.max(8, baseScoring - 10);
      let runningWinRate = 42;
      let runningEff = 10;
      let runningXp = 500;

      for (let i = 11; i >= 0; i--) {
        const d = new Date(today);
        d.setMonth(d.getMonth() - i * 2); // 2-month steps over ~2 years

        const stepSeed = Math.sin(i * 0.8) * 2;
        runningScoring = Math.min(36, Math.max(8, Number((runningScoring + 1.2 + stepSeed * 0.3).toFixed(1))));
        runningWinRate = Math.min(85, Math.max(40, Math.round(runningWinRate + 2.5 + stepSeed)));
        runningEff = Math.min(30, Math.max(10, Number((runningEff + 1.1).toFixed(1))));
        runningXp += 650 + Math.round(Math.abs(stepSeed) * 100);

        const monthName = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

        points.push({
          date: d,
          dateStr: monthName,
          scoringAvg: runningScoring,
          winRate: runningWinRate,
          efficiency: runningEff,
          xpEarned: runningXp,
          gamesCount: 8 + (i % 3),
        });
      }
      return points;
    }

    // Season 30-day window (Current vs Last Season)
    const yearOffset = season === 'last' ? 1 : 0;
    const scoringModifier = season === 'last' ? -4.5 : 0;
    const winRateModifier = season === 'last' ? -12 : 0;

    let runningScoring = Math.max(8, baseScoring - 4 + scoringModifier);
    let runningWinRate = Math.max(35, 54 + winRateModifier);
    let runningEff = Math.max(10, 14 + (season === 'last' ? -3 : 0));
    let runningXp = season === 'last' ? 2400 : 0;

    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      if (yearOffset > 0) {
        d.setFullYear(d.getFullYear() - yearOffset);
      }
      d.setDate(d.getDate() - i);

      const daySeed = Math.sin(i * 0.7 + d.getDate()) * 2.5;
      runningScoring = Math.min(38, Math.max(8, Number((runningScoring + daySeed * 0.4 + (29 - i) * 0.12).toFixed(1))));
      runningWinRate = Math.min(88, Math.max(35, Math.round(runningWinRate + (i % 3 === 0 ? 3 : -2) + daySeed)));
      runningEff = Math.min(32, Math.max(10, Number((runningEff + daySeed * 0.3 + 0.1).toFixed(1))));

      const dailyGames = (i % 2 === 0 || i % 5 === 0) ? Math.floor(Math.abs(daySeed)) + 1 : 0;
      runningXp += dailyGames * 75 + Math.round(Math.abs(daySeed) * 20);

      const monthName = d.toLocaleDateString('en-US', { month: 'short' });
      const dayNum = d.getDate();

      points.push({
        date: d,
        dateStr: `${monthName} ${dayNum}`,
        scoringAvg: runningScoring,
        winRate: runningWinRate,
        efficiency: runningEff,
        xpEarned: runningXp,
        gamesCount: dailyGames,
      });
    }

    return points;
  };

  const data = generateSeasonData();

  const getMetricDetails = () => {
    switch (metric) {
      case 'scoringAvg':
        return {
          label: selectedSport === 'basketball' ? 'Scoring Average (PPG)' : 'Production / Game',
          color: '#A3E635', // Lime-400
          unit: selectedSport === 'basketball' ? 'PTS' : 'AVG',
          gradientId: 'scoringGradient',
        };
      case 'winRate':
        return {
          label: 'Win Rate %',
          color: '#38BDF8', // Sky-400
          unit: '%',
          gradientId: 'winRateGradient',
        };
      case 'efficiency':
        return {
          label: 'Player Efficiency Rating (PER)',
          color: '#F43F5E', // Rose-500
          unit: 'PER',
          gradientId: 'effGradient',
        };
      case 'xpEarned':
        return {
          label: 'Cumulative XP Earned',
          color: '#C084FC', // Purple-400
          unit: 'XP',
          gradientId: 'xpGradient',
        };
    }
  };

  const metricDetails = getMetricDetails();

  // D3 Chart Render Effect
  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth || 600;
    const height = 280;
    const margin = { top: 20, right: 30, bottom: 40, left: 50 };

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Clear previous SVG contents
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    svg
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Define X & Y scales
    const xScale = d3
      .scaleTime()
      .domain(d3.extent(data, (d) => d.date) as [Date, Date])
      .range([0, innerWidth]);

    const yMin = d3.min(data, (d) => d[metric]) as number;
    const yMax = d3.max(data, (d) => d[metric]) as number;
    const yPadding = (yMax - yMin) * 0.15 || 5;

    const yScale = d3
      .scaleLinear()
      .domain([Math.max(0, yMin - yPadding), yMax + yPadding])
      .nice()
      .range([innerHeight, 0]);

    // Gradients
    const defs = svg.append('defs');
    const gradient = defs
      .append('linearGradient')
      .attr('id', metricDetails.gradientId)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');

    gradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', metricDetails.color)
      .attr('stop-opacity', 0.4);

    gradient
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', metricDetails.color)
      .attr('stop-opacity', 0.0);

    // Grid lines
    const yAxisGrid = d3
      .axisLeft(yScale)
      .tickSize(-innerWidth)
      .tickFormat(() => '')
      .ticks(5);

    g.append('g')
      .attr('class', 'grid-lines')
      .call(yAxisGrid)
      .selectAll('line')
      .attr('stroke', '#312E81')
      .attr('stroke-opacity', 0.3)
      .attr('stroke-dasharray', '3,3');

    // Bottom Axis (Dates)
    const xAxis = d3
      .axisBottom(xScale)
      .ticks(6)
      .tickFormat((d) => d3.timeFormat('%b %d')(d as Date));

    const xAxisGroup = g
      .append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis);

    xAxisGroup.selectAll('text')
      .attr('fill', '#A5B4FC')
      .attr('font-size', '11px')
      .attr('font-weight', '700');

    xAxisGroup.selectAll('line').attr('stroke', '#312E81');
    xAxisGroup.select('.domain').attr('stroke', '#312E81');

    // Left Axis (Values)
    const yAxis = d3.axisLeft(yScale).ticks(5);

    const yAxisGroup = g.append('g').call(yAxis);

    yAxisGroup.selectAll('text')
      .attr('fill', '#A5B4FC')
      .attr('font-size', '11px')
      .attr('font-weight', '700');

    yAxisGroup.selectAll('line').attr('stroke', '#312E81');
    yAxisGroup.select('.domain').attr('stroke', '#312E81');

    // Area Generator
    const areaGenerator = d3
      .area<ProgressDataPoint>()
      .x((d) => xScale(d.date))
      .y0(innerHeight)
      .y1((d) => yScale(d[metric]))
      .curve(d3.curveMonotoneX);

    // Line Generator
    const lineGenerator = d3
      .line<ProgressDataPoint>()
      .x((d) => xScale(d.date))
      .y((d) => yScale(d[metric]))
      .curve(d3.curveMonotoneX);

    // Append Gradient Area Fill
    g.append('path')
      .datum(data)
      .attr('fill', `url(#${metricDetails.gradientId})`)
      .attr('d', areaGenerator);

    // Append Path Line
    const path = g
      .append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', metricDetails.color)
      .attr('stroke-width', 3)
      .attr('d', lineGenerator);

    // Path entrance animation
    const totalLength = (path.node() as SVGPathElement)?.getTotalLength() || 1000;
    path
      .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
      .attr('stroke-dashoffset', totalLength)
      .transition()
      .duration(900)
      .ease(d3.easeCubicOut)
      .attr('stroke-dashoffset', 0);

    // Append Data Points Dots
    g.selectAll('.data-dot')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'data-dot')
      .attr('cx', (d) => xScale(d.date))
      .attr('cy', (d) => yScale(d[metric]))
      .attr('r', 3.5)
      .attr('fill', metricDetails.color)
      .attr('stroke', '#0B0F2A')
      .attr('stroke-width', 1.5);

    // Hover Crosshair & Tooltip Overlay
    const focusLineGroup = g.append('g').style('display', 'none');

    // Vertical dashed hover line
    const hoverLine = focusLineGroup
      .append('line')
      .attr('stroke', '#A3E635')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4,4')
      .attr('y1', 0)
      .attr('y2', innerHeight);

    // Hover Highlight Circle
    const hoverCircle = focusLineGroup
      .append('circle')
      .attr('r', 6)
      .attr('fill', metricDetails.color)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 2);

    // Transparent overlay rectangle to capture cursor
    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .on('mouseenter', () => focusLineGroup.style('display', null))
      .on('mouseleave', () => {
        focusLineGroup.style('display', 'none');
        setTooltipData(null);
      })
      .on('mousemove', (event) => {
        const [mouseX] = d3.pointer(event);
        const xDate = xScale.invert(mouseX);

        // Bisect to find nearest data index
        const bisect = d3.bisector<ProgressDataPoint, Date>((d) => d.date).left;
        const idx = bisect(data, xDate, 1);
        const d0 = data[idx - 1];
        const d1 = data[idx];
        let d = d0;
        if (d1) {
          d = xDate.getTime() - d0.date.getTime() > d1.date.getTime() - xDate.getTime() ? d1 : d0;
        }

        if (!d) return;

        const cx = xScale(d.date);
        const cy = yScale(d[metric]);

        hoverLine.attr('x1', cx).attr('x2', cx);
        hoverCircle.attr('cx', cx).attr('cy', cy);

        setTooltipData({
          dateStr: d.dateStr,
          value: d[metric],
          metricLabel: metricDetails.label,
          gamesCount: d.gamesCount,
          x: cx + margin.left,
          y: cy + margin.top,
        });
      });
  }, [metric, selectedSport, season]);

  const latestVal = data[data.length - 1][metric];
  const firstVal = data[0][metric];
  const pctChange = firstVal > 0 ? Math.round(((latestVal - firstVal) / firstVal) * 100) : 0;

  const seasonLabels = {
    current: 'Current Season (2026)',
    last: 'Last Season (2025)',
    alltime: 'All-Time Career Overview',
  };

  return (
    <ProRestrictedWrapper
      user={user}
      onOpenProCheckout={onOpenProCheckout}
      featureTitle="30-Day D3 Trajectory & Performance Spline"
      featureDescription="Unlock D3.js interactive career progression curves and custom metric timelines."
      upsellMessage="This historical performance deep-dive requires a PRO membership to unlock."
    >
      <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-5 text-white">
      {/* Chart Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-white/10 pb-4">
        <div>
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-lime-400 stroke-[2.5]" />
            <h2 className="text-lg font-black italic uppercase text-white">
              D3.js Player Progress Trajectory
            </h2>
          </div>
          <p className="text-xs text-indigo-200/70 font-semibold mt-0.5">
            Vector-rendered spline tracking individual progress trend for <strong className="text-lime-400">{seasonLabels[season]}</strong>
          </p>
        </div>

        {/* Filter Controls Row: Season Dropdown + Metric Tabs */}
        <div className="flex flex-wrap items-center gap-2">
          {/* SEASON DROPDOWN FILTER */}
          <div className="flex items-center space-x-2 bg-indigo-950 px-3 py-1.5 rounded-2xl border border-white/10 shadow-inner">
            <Target className="w-4 h-4 text-lime-400 shrink-0" />
            <span className="text-[10px] font-black italic uppercase text-indigo-300/80 shrink-0">Season:</span>
            <select
              value={season}
              onChange={(e) => setSeason(e.target.value as SeasonFilter)}
              className="bg-transparent text-white text-xs font-black italic uppercase focus:outline-none cursor-pointer pr-1"
            >
              <option value="current" className="bg-indigo-950 text-white">Current Season</option>
              <option value="last" className="bg-indigo-950 text-white">Last Season</option>
              <option value="alltime" className="bg-indigo-950 text-white">All-Time</option>
            </select>
          </div>

          {/* Metric Selector Tabs */}
          <div className="flex items-center bg-indigo-950 p-1 rounded-2xl border border-white/10 gap-1 overflow-x-auto max-w-full">
            <button
              onClick={() => setMetric('scoringAvg')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition shrink-0 ${
                metric === 'scoringAvg'
                  ? 'bg-lime-400 text-black shadow-md'
                  : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Scoring Avg 🏀
            </button>
            <button
              onClick={() => setMetric('winRate')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition shrink-0 ${
                metric === 'winRate'
                  ? 'bg-sky-400 text-black shadow-md'
                  : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Win Rate % 🏆
            </button>
            <button
              onClick={() => setMetric('efficiency')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition shrink-0 ${
                metric === 'efficiency'
                  ? 'bg-rose-500 text-white shadow-md'
                  : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Efficiency (PER) ⚡
            </button>
            <button
              onClick={() => setMetric('xpEarned')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition shrink-0 ${
                metric === 'xpEarned'
                  ? 'bg-purple-500 text-white shadow-md'
                  : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Total XP ⭐
            </button>
          </div>
        </div>
      </div>

      {/* STAT SUMMARY HIGHLIGHT CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-bold">
        <div className="bg-indigo-950/80 p-3.5 rounded-2xl border border-white/5 space-y-1">
          <span className="text-[10px] text-indigo-300 uppercase">Season Current</span>
          <p className="text-xl font-black italic text-lime-400">
            {latestVal} <span className="text-xs text-indigo-200">{metricDetails.unit}</span>
          </p>
        </div>

        <div className="bg-indigo-950/80 p-3.5 rounded-2xl border border-white/5 space-y-1">
          <span className="text-[10px] text-indigo-300 uppercase">Season Trend</span>
          <p className={`text-xl font-black italic ${pctChange >= 0 ? 'text-lime-400' : 'text-rose-400'}`}>
            {pctChange >= 0 ? `+${pctChange}%` : `${pctChange}%`}
          </p>
        </div>

        <div className="bg-indigo-950/80 p-3.5 rounded-2xl border border-white/5 space-y-1">
          <span className="text-[10px] text-indigo-300 uppercase">Season Peak</span>
          <p className="text-xl font-black italic text-sky-400">
            {Math.max(...data.map((d) => d[metric]))} <span className="text-xs text-indigo-200">{metricDetails.unit}</span>
          </p>
        </div>

        <div className="bg-indigo-950/80 p-3.5 rounded-2xl border border-white/5 space-y-1">
          <span className="text-[10px] text-indigo-300 uppercase">Consistency Index</span>
          <p className="text-xl font-black italic text-amber-300">
            {season === 'current' ? '94.2' : season === 'last' ? '88.5' : '96.0'} <span className="text-xs text-indigo-200">/100</span>
          </p>
        </div>
      </div>

      {/* SVG D3 Container */}
      <div ref={containerRef} className="relative w-full overflow-hidden pt-2">
        <svg ref={svgRef} className="w-full h-[280px] overflow-visible" />

        {/* Floating D3 Custom Tooltip Box */}
        {tooltipData && (
          <div
            className="absolute pointer-events-none bg-indigo-950/95 border border-lime-400/50 p-3 rounded-2xl shadow-2xl backdrop-blur-md text-xs space-y-1 z-20 transition-all duration-75"
            style={{
              left: Math.min(tooltipData.x + 10, (containerRef.current?.clientWidth || 400) - 160),
              top: Math.max(10, tooltipData.y - 70),
            }}
          >
            <div className="flex items-center justify-between space-x-3 text-[10px] font-black text-lime-300">
              <span>{tooltipData.dateStr}</span>
              {tooltipData.gamesCount > 0 && (
                <span className="px-1.5 py-0.5 bg-lime-400/20 text-lime-400 rounded">
                  {tooltipData.gamesCount} Game{tooltipData.gamesCount > 1 ? 's' : ''}
                </span>
              )}
            </div>

            <p className="font-black italic text-sm text-white">
              {tooltipData.value} <span className="text-xs font-normal text-indigo-200">{metricDetails.unit}</span>
            </p>
            <p className="text-[10px] text-indigo-300 font-medium">{tooltipData.metricLabel}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-[11px] text-indigo-300/70 font-semibold border-t border-white/10 pt-3">
        <span className="flex items-center">
          <Calendar className="w-3.5 h-3.5 mr-1 text-lime-400" /> 30-Day Window: {data[0].dateStr} - {data[data.length - 1].dateStr}
        </span>
        <span className="flex items-center text-lime-400 font-bold">
          <Sparkles className="w-3.5 h-3.5 mr-1" /> D3.js Monotone Spline Curve
        </span>
      </div>
    </div>
    </ProRestrictedWrapper>
  );
};
